/**
 * Lifespan rules for "Hey, I'm interested" signals.
 *
 * An interest is a nudge, not a record — it only means something while both the
 * pedal and the intent behind it are still current. These two numbers decide
 * when a sender may nudge again and when the signal stops counting at all.
 *
 * The server enforces expiry (functions/cleanupListingInterest.js keeps its own
 * copy of the window, since Cloud Functions can't import from src). The client
 * applies the same cutoff so the UI doesn't show interests the next cleanup run
 * is about to delete.
 */

// How long "Interest sent" sticks before the sender may raise a hand again.
// Short on purpose: an owner who never answers shouldn't be able to lock someone
// out of a listing by ignoring them.
export const INTEREST_RESEND_AFTER_MS = 60 * 60 * 1000;

// After this, the signal is stale. Two weeks of silence is an answer.
export const INTEREST_EXPIRY_DAYS = 14;
export const INTEREST_EXPIRY_MS = INTEREST_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * Is the pedal an interest was sent about still gettable?
 *
 * Mirrors isListingAvailable in functions/matchValidation.js — the server does
 * the real enforcement (cleanupListingInterest.js clears the interest the
 * moment a listing crosses this line), so this copy exists only to catch the
 * seconds between the write and the trigger, and the rarer case of a page left
 * open across the change. Change both together.
 *
 * Every field is compared against an exact value rather than truthiness, which
 * is what the server does and the reason matters in both directions: legacy
 * listing docs predate all three fields, and a missing one has always meant
 * available. Reading them loosely would strand a healthy listing behind the
 * unavailable warning on nothing more than an absent key.
 */
export function isListingStillAvailable(listing) {
  if (!listing) return false;
  if (listing.active === false) return false;
  if (listing.inTrade === true) return false;
  if (listing.needsReactivation === true) return false;
  return true;
}

/** Is this interest still inside its useful life? */
export function isInterestFresh(createdAt) {
  const sentAt = createdAt?.toDate?.() || (createdAt instanceof Date ? createdAt : null);
  if (!sentAt) return true; // Missing timestamp: don't hide it on a guess.
  return Date.now() - sentAt.getTime() < INTEREST_EXPIRY_MS;
}
